import * as React from 'react';
import { dayLabel } from '../format';
import type { ActivityItem as ActivityItemType, Audience } from '../types';
import { ActivityItem } from './ActivityItem';

type ActivityFeedProps = {
  items: ActivityItemType[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onOpen: (item: ActivityItemType) => void;
  onLoadMore: () => void;
  audience?: Audience | null;
};

export const SKELETON_ROWS = 4;

const groupByDay = (items: ActivityItemType[]): { label: string; items: ActivityItemType[] }[] => {
  const now = new Date();
  const groups: { label: string; items: ActivityItemType[] }[] = [];
  items.forEach((item) => {
    const label = dayLabel(item.occurred_at, now);
    const last = groups[groups.length - 1];
    if (last && last.label === label) {
      last.items.push(item);
    } else {
      groups.push({ label, items: [item] });
    }
  });
  return groups;
};

export function FeedSkeleton({ rows = SKELETON_ROWS }: { rows?: number }) {
  return (
    <div className="activity-dashboard__skeleton" role="status" aria-busy="true">
      <span className="visually-hidden">Loading activity</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="activity-skeleton" aria-hidden="true">
          <span className="activity-skeleton__icon" />
          <span className="activity-skeleton__lines">
            <span className="activity-skeleton__line activity-skeleton__line--title" />
            <span className="activity-skeleton__line activity-skeleton__line--body" />
          </span>
          <span className="activity-skeleton__line activity-skeleton__line--time" />
        </div>
      ))}
    </div>
  );
}

export function ActivityFeed({
  items,
  loading,
  loadingMore,
  hasMore,
  onOpen,
  onLoadMore,
  audience = null
}: ActivityFeedProps) {
  const sentinelRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!hasMore || loading || loadingMore) return undefined;
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) onLoadMore();
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, onLoadMore]);

  if (loading && items.length === 0) {
    return <FeedSkeleton />;
  }

  const groups = groupByDay(items);

  return (
    <div className="activity-dashboard__list">
      {groups.map((group) => (
        <section key={group.label} className="activity-dashboard__day">
          <h3 className="activity-dashboard__day-label">{group.label}</h3>
          {group.items.map((item) => (
            <ActivityItem key={item.id} item={item} onOpen={onOpen} audience={audience} />
          ))}
        </section>
      ))}
      {hasMore ? <div ref={sentinelRef} className="activity-dashboard__sentinel" /> : null}
      {hasMore || loadingMore ? (
        <div className="activity-dashboard__more">
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading more…' : 'Load more'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
